/*
 * Individual day cell. Cream rounded rectangle with the day number
 * top-right and color-coded holiday/event pills stacked at the bottom.
 * Today / selected get a full orange fill with white type. Off-month
 * days fade to a transparent background and gray number.
 */

.cell {
  position: relative;
  background: rgba(255, 248, 232, 0.85);
  border: 1px solid color-mix(in srgb, var(--token-accent) 18%, transparent);
  border-radius: 14px;
  padding: 8px 10px 10px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
  cursor: pointer;
  text-align: left;
  min-width: 0;
  min-height: 0;
  transition:
    background var(--token-motion-control-ms) var(--token-motion-control-easing),
    border-color var(--token-motion-control-ms) var(--token-motion-control-easing);
}

.cell:hover {
  background: rgba(255, 248, 232, 1);
  border-color: color-mix(in srgb, var(--token-accent) 40%, transparent);
}

.cell[data-in-month='false'] {
  background: transparent;
  border-color: transparent;
  cursor: default;
}

.cell[data-in-month='false']:hover {
  background: transparent;
  border-color: transparent;
}

.cell[data-in-month='false'] .dayNumber {
  color: rgba(28, 38, 32, 0.28);
  font-weight: 500;
}

/* Federal holidays get a translucent accent wash on the cell itself,
 * so the "yes, this day is special" cue reads from across the room
 * AND coordinates with the active theme's accent color. */
.cell[data-has-federal='true']:not([data-today='true']):not([data-selected='true']) {
  background: color-mix(in srgb, var(--token-accent) 47%, transparent);
  border-color: color-mix(in srgb, var(--token-accent) 55%, transparent);
}

/* Today / selected: full accent fill, white type. */
.cell[data-today='true'],
.cell[data-selected='true'] {
  background: var(--token-accent);
  border-color: var(--token-accent);
  color: #fff;
}

.cell[data-today='true']:hover,
.cell[data-selected='true']:hover {
  background: color-mix(in srgb, var(--token-accent) 85%, #000);
  border-color: color-mix(in srgb, var(--token-accent) 85%, #000);
}

.dayNumber {
  align-self: flex-end;
  font-family: var(--token-font-display);
  font-size: clamp(18px, 1.4vw, 24px);
  font-weight: 700;
  line-height: 1;
  color: inherit;
}

.pills {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: auto;
  min-width: 0;
}

.pill {
  font-family: var(--token-font-ui);
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Color-coded by source — purple for fun holidays, green for class
 * events, peach for federal holidays. Soft, kid-readable shades.
 * Bumped ~25 points more solid so the pill backgrounds carry the
 * label across busier theme bgs. */
.pillFun {
  background: rgba(184, 158, 220, 0.67);
  color: rgba(78, 50, 130, 0.95);
}

.pillEvent {
  background: rgba(154, 192, 138, 0.67);
  color: rgba(60, 100, 50, 0.95);
}

.pillFederal {
  background: color-mix(in srgb, var(--token-accent) 65%, transparent);
  color: #000;
}

/* When the cell is highlighted (today/selected), pills sit on the
 * orange fill. Translucent white still — but 25 pts more solid for
 * legibility. */
.cell[data-today='true'] .pill,
.cell[data-selected='true'] .pill {
  background: rgba(255, 255, 255, 0.53);
  color: #fff;
}

/* Birthday image fills ~85% of the cell, centered behind the day
 * number + pills (z-index 0) so the celebration cue reads from across
 * the room but the date + holiday/event labels stay legible.
 * `object-fit: contain` preserves the artwork's aspect ratio inside
 * the 85%-square box. The source PNG (Birthday-transparent.png) has
 * a real alpha channel so we no longer need the mix-blend-mode hack
 * — colors stay true on cream cells, federal-holiday peach cells, and
 * the orange today/selected fill alike. */
.birthday {
  position: absolute;
  inset: 7.5%;
  width: 85%;
  height: 85%;
  object-fit: contain;
  pointer-events: none;
  z-index: 0;
  user-select: none;
}

/* Lift the day number + pill stack above the birthday image. */
.cell .dayNumber,
.cell .pills {
  position: relative;
  z-index: 1;
}
